import { useState, useEffect, useRef } from 'react';
import { LinkIcon, DocumentTextIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { postShowData } from '../../../util/util';
import {
  uploadShowImage,
  uploadShowPreviewPdf,
  removeShowPreviewPdf,
  getShowPreviewPdfUrl,
} from '../../../util/show-images';
import useFieldErrors, { missingFieldErrors } from '../../../hooks/useFieldErrors';
import ManageShowImagesModal from './ManageShowImagesModal';
import ManagePreviewPdfModal, { pdfDisplayName } from './ManagePreviewPdfModal';
import ToggleButton from '../ui/ToggleButton';
// import TimeRangePicker from '../pickers/TimeRangePicker';
import TimeRangeSelect from '../pickers/TimeRangeSelect';
import DatePicker from '../pickers/DatePicker';
import ExpandableTextarea from '../ui/ExpandableTextarea';
import ImagePreviewCarousel from '../ImagePreviewCarousel';
import Modal from './Modal';
import ModalActions from './ModalActions';
import FormField, { TextField } from '../ui/FormField';
import { useToast } from '../../ui/Toast';
import { IconTooltip } from '../ui/TooltipCard';
import { buildDisplayTitle } from '../../../util/data-factory';

const DEFAULT_LINK_OUT_TEXT = 'See the online viewing room';

function parseArtistNames(text) {
  return (text || '')
    .split(',')
    .map(name => name.trim())
    .filter(Boolean);
}

function isValidDate(str) {
  if (!str) return false;
  const d = new Date(str);
  if (isNaN(d.getTime())) return false;
  const [y, m, day] = str.split('-').map(Number);
  return d.getUTCFullYear() === y && d.getUTCMonth() + 1 === m && d.getUTCDate() === day;
}

function EditShowModal({
  isModalOpen,
  setIsModalOpen,
  selectedItem,
  selectedVenueId,
  onShowSaved,
  onShowImagesSaved,
}) {
  const isEdit = !!selectedItem;
  const { showSuccess, showError } = useToast();
  const { errors, setErrors, clearError } = useFieldErrors();
  const [isSaving, setIsSaving] = useState(false);
  const [isImagesModalOpen, setIsImagesModalOpen] = useState(false);
  const [currentImageUrls, setCurrentImageUrls] = useState([]);
  const [pendingEntries, setPendingEntries] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [showTitle, setShowTitle] = useState('');
  const [artistNamesText, setArtistNamesText] = useState('');
  const [description, setDescription] = useState('');
  const [openingStartTime, setOpeningStartTime] = useState(null);
  const [openingEndTime, setOpeningEndTime] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [openingDate, setOpeningDate] = useState(null);
  const [linkOut, setLinkOut] = useState('');
  const [linkOutText, setLinkOutText] = useState('');
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfPath, setPdfPath] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const hasPdf = !!(pdfFile || pdfPath);

  // reset on every open, not just on a new show, so a cancelled edit doesn't come back
  useEffect(() => {
    if (!isModalOpen) return;
    setErrors({});
    setPdfPath(selectedItem?.preview_pdf_path ?? null);
    setPdfFile(null);
    setCurrentImageUrls(selectedItem?.image_urls ?? []);
    setPreviewIndex(0);
    setShowTitle(selectedItem?.show_title || '');
    setArtistNamesText((selectedItem?.artist_names || []).join(', '));
    // short_description is no longer used; it's only kept in sync on save
    setDescription(selectedItem?.long_description || selectedItem?.short_description || '');
    setOpeningStartTime(selectedItem?.opening_start_time?.slice(0, 5) || null);
    setOpeningEndTime(selectedItem?.opening_end_time?.slice(0, 5) || null);
    setStartDate(selectedItem?.start_date || null);
    setEndDate(selectedItem?.end_date || null);
    setOpeningDate(selectedItem?.opening_date || null);
    setLinkOut(selectedItem?.link_out || '');
    setLinkOutText(selectedItem?.link_out_text || '');
  }, [selectedItem, isModalOpen, setErrors]);

  const displayTitlePreview =
    showTitle.trim() || artistNamesText.trim()
      ? buildDisplayTitle(showTitle, parseArtistNames(artistNamesText), selectedItem?.display_title)
      : null;

  const pdfName = pdfFile?.name ?? (pdfPath && pdfDisplayName(pdfPath));
  // an unsaved PDF has no stored copy to link to yet
  const previewHref = hasPdf ? (!pdfFile && getShowPreviewPdfUrl(pdfPath)) || null : linkOut;

  // to clean up when images are left unsaved
  const prevPendingRef = useRef([]);
  useEffect(() => {
    const currentUrls = new Set(pendingEntries.map(e => e.previewUrl));
    prevPendingRef.current.forEach(entry => {
      if (!currentUrls.has(entry.previewUrl)) {
        URL.revokeObjectURL(entry.previewUrl);
      }
    });
    prevPendingRef.current = pendingEntries;
  }, [pendingEntries]);

  useEffect(() => {
    if (!isModalOpen) setPendingEntries([]);
  }, [isModalOpen]);

  const displayUrls = isEdit
    ? currentImageUrls
    : pendingEntries.map(e => e.previewUrl);

  const validateDates = () => {
    const newErrors = {};
    if (!startDate) newErrors.startDate = 'Please enter a start date';
    else if (!isValidDate(startDate)) newErrors.startDate = 'Please enter a valid start date';
    if (!endDate) newErrors.endDate = 'Please enter an end date';
    else if (!isValidDate(endDate)) newErrors.endDate = 'Please enter a valid end date';
    if (!openingDate) newErrors.openingDate = 'Please enter an opening date';
    else if (!isValidDate(openingDate)) newErrors.openingDate = 'Please enter a valid opening date';

    if (!newErrors.startDate && !newErrors.endDate && endDate < startDate) {
      newErrors.endDate = 'End date must be on or after start date';
    }
    if (!newErrors.startDate && !newErrors.openingDate && openingDate < startDate) {
      newErrors.openingDate = 'Opening date must be on or after start date';
    }
    if (!newErrors.endDate && !newErrors.openingDate && openingDate > endDate) {
      newErrors.openingDate = 'Opening date must be on or before end date';
    }
    return newErrors;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (isSaving) return;
    const formData = new FormData(e.target);

    const newErrors = {
      ...missingFieldErrors(formData, ['showTitle', 'artistNames', 'description']),
      ...validateDates(),
    };
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    const artistNames = parseArtistNames(formData.get('artistNames'));
    const descriptionText = formData.get('description');

    const showData = {
      show_title: showTitle,
      display_title: buildDisplayTitle(showTitle, artistNames, selectedItem?.display_title),
      start_date: startDate,
      end_date: endDate,
      opening_date: openingDate || null,
      opening_start_time: openingStartTime,
      opening_end_time: openingEndTime,
      link_out: hasPdf ? null : linkOut || null,
      link_out_text: formData.get('linkOutText') || null,
      artist_names: artistNames,
      artist_count: artistNames.length,
      long_description: descriptionText,
      short_description: descriptionText,
      venue_id: selectedVenueId,
    };

    setIsSaving(true);
    try {
      if (isEdit) {
        const showId = selectedItem.show_id;
        const savedPdfPath = selectedItem.preview_pdf_path ?? null;
        const uploadedPdfPath = pdfFile ? await uploadShowPreviewPdf(showId, pdfFile) : null;
        const previewPdfPath = uploadedPdfPath ?? pdfPath;
        try {
          await postShowData({ id: showId, ...showData, preview_pdf_path: previewPdfPath }, false);
        } catch (error) {
          if (uploadedPdfPath) removeShowPreviewPdf(uploadedPdfPath);
          throw error;
        }
        if (savedPdfPath && savedPdfPath !== previewPdfPath) removeShowPreviewPdf(savedPdfPath);
        onShowSaved?.(
          { ...selectedItem, ...showData, preview_pdf_path: previewPdfPath, image_urls: currentImageUrls },
          false
        );
      } else {
        const created = await postShowData(showData, true);
        if (!created) {
          setIsModalOpen(false);
          return;
        }

        const showId = created.show_id;
        const followUp = {};
        if (pendingEntries.length > 0) {
          // to upload all the photos first, and only save the list after all succeeded
          const uploadedUrls = await Promise.all(pendingEntries.map(({ file }) => uploadShowImage(showId, file)));
          followUp.image_urls = [...(created.image_urls ?? []), ...uploadedUrls];
        }
        if (pdfFile) {
          followUp.preview_pdf_path = await uploadShowPreviewPdf(showId, pdfFile);
        }
        if (Object.keys(followUp).length > 0) {
          await postShowData({ id: showId, ...followUp }, false);
        }

        onShowSaved?.({ ...created, image_urls: created.image_urls ?? [], ...followUp }, true);
      }
      setIsModalOpen(false);
      showSuccess('Show saved.');
    } catch (error) {
      console.error('Error saving show:', error);
      showError(`Failed to save show: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => !isSaving && setIsModalOpen(false)}
      title={isEdit ? 'Edit Show' : 'Add Show'}
      clipContent={false}
    >
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Column 1 */}
          <div className="flex flex-col gap-4">
            <div>
              <TextField
                label="Show Title"
                name="showTitle"
                required
                info={
                  <IconTooltip
                    position="bottom-right"
                    label={<>Solo shows are listed under the artist's name.<br />Shows with 2+ artists use this title.</>}
                  >
                    <InformationCircleIcon className="h-3.5 w-3.5 text-slate-300" aria-hidden="true" />
                  </IconTooltip>
                }
                error={errors.showTitle}
                value={showTitle}
                onChange={e => { setShowTitle(e.target.value); clearError('showTitle'); }}
              />
              {displayTitlePreview && (
                <p className="text-xs text-slate-400 mt-1 truncate">
                  Listed as: <span className="text-slate-700">{displayTitlePreview}</span>
                </p>
              )}
            </div>

            <FormField label="Start Date" required>
              <DatePicker
                value={startDate}
                onChange={v => { setStartDate(v); clearError('startDate'); }}
                error={errors.startDate}
              />
            </FormField>

            <FormField label="End Date" required>
              <DatePicker
                value={endDate}
                onChange={v => { setEndDate(v); clearError('endDate'); }}
                error={errors.endDate}
              />
            </FormField>

            <FormField label="Opening Date" required>
              <DatePicker
                value={openingDate}
                onChange={v => { setOpeningDate(v); clearError('openingDate'); }}
                error={errors.openingDate}
              />
            </FormField>

            <FormField label="Opening Hours">
              {/* <TimeRangePicker
                startValue={openingStartTime}
                onStartChange={setOpeningStartTime}
                endValue={openingEndTime}
                onEndChange={setOpeningEndTime}
              /> */}
              <TimeRangeSelect
                startValue={openingStartTime}
                onStartChange={setOpeningStartTime}
                endValue={openingEndTime}
                onEndChange={setOpeningEndTime}
                defaultHighlight="18:00"
              />
            </FormField>
          </div>

          {/* Column 2 */}
          <div className="flex flex-col gap-4">
            <TextField
              label="Artist Name(s)"
              name="artistNames"
              required
              error={errors.artistNames}
              placeholder="Separate with a comma, i.e. John Doe, Jane Smith, etc."
              value={artistNamesText}
              onChange={e => { setArtistNamesText(e.target.value); clearError('artistNames'); }}
            />

            <FormField label="Description" htmlFor="description" required>
              <ExpandableTextarea
                id="description"
                name="description"
                required
                title="Description"
                value={description}
                onChange={e => { setDescription(e.target.value); clearError('description'); }}
                error={errors.description}
                className="h-[112px]"
              />
            </FormField>

            <FormField label={`Image${displayUrls.length > 0 ? ` (${displayUrls.length})` : ''}`}>
              <ImagePreviewCarousel
                urls={displayUrls}
                index={previewIndex}
                onIndexChange={setPreviewIndex}
                onManage={() => setIsImagesModalOpen(true)}
              />
            </FormField>
          </div>

          <div className="col-span-1 md:col-span-2 bg-white rounded-2xl px-6 py-4 border border-secondary-red/30 hover:border-secondary-red transition-colors">
            <div className="flex items-center gap-2 mb-4">
              <LinkIcon className={`h-4 w-4 shrink-0 ${previewHref ? 'text-secondary-red' : 'text-slate-300'}`} />
              {previewHref ? (
                <a
                  href={previewHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-secondary-red underline hover:opacity-80 transition"
                >
                  {linkOutText || DEFAULT_LINK_OUT_TEXT}
                </a>
              ) : (
                <span className="text-sm font-medium text-slate-400">
                  {linkOutText || DEFAULT_LINK_OUT_TEXT}
                </span>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <TextField
                label="Preview Text"
                name="linkOutText"
                className="flex-1"
                placeholder={DEFAULT_LINK_OUT_TEXT}
                value={linkOutText}
                onChange={e => setLinkOutText(e.target.value)}
              />
              <FormField label="Preview URL or PDF" htmlFor="linkOut" className="flex-1 min-w-0">
                <div className="join w-full">
                  <input
                    id="linkOut"
                    name="linkOut"
                    type="url"
                    className="input input-bordered join-item flex-1 min-w-0 text-sm py-2 px-3 h-9 focus:outline-none"
                    value={hasPdf ? '' : linkOut}
                    disabled={hasPdf}
                    placeholder={hasPdf ? `Using PDF: ${pdfName}` : undefined}
                    onChange={e => setLinkOut(e.target.value)}
                  />
                  <ToggleButton
                    active={hasPdf}
                    inactiveClass="bg-white text-slate-700 input-bordered hover:bg-black hover:text-white hover:border-black"
                    onClick={() => setIsPdfModalOpen(true)}
                    disabled={isSaving}
                    title={hasPdf ? pdfName : 'Upload a PDF preview'}
                    aria-label={hasPdf ? `PDF preview: ${pdfName}` : 'Upload a PDF preview'}
                  >
                    <DocumentTextIcon className="h-4 w-4" />
                  </ToggleButton>
                </div>
              </FormField>
            </div>
          </div>
        </div>

        <ModalActions onCancel={() => setIsModalOpen(false)} isLoading={isSaving} />
      </form>

      <ManageShowImagesModal
        isOpen={isImagesModalOpen}
        setIsOpen={setIsImagesModalOpen}
        selectedItem={selectedItem}
        currentImageUrls={currentImageUrls}
        onImagesSaved={finalUrls => {
          setCurrentImageUrls(finalUrls);
          setPreviewIndex(0);
          // already saved to the db, so the list row must update even if this form is cancelled
          onShowImagesSaved?.(selectedItem.show_id, finalUrls);
        }}
        pendingEntries={pendingEntries}
        onPendingSaved={setPendingEntries}
      />
      <ManagePreviewPdfModal
        isOpen={isPdfModalOpen}
        setIsOpen={setIsPdfModalOpen}
        pdfFile={pdfFile}
        pdfPath={pdfPath}
        onPdfSaved={({ file, path }) => {
          setPdfFile(file);
          setPdfPath(path);
        }}
      />
    </Modal>
  );
}

export default EditShowModal;
